import { useEffect } from 'react';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import ivclass from '@invideoio/web-shared/Modules/IVClass';

import { TableOfContentType } from '../../types';
import { getAssetUrl } from '@utils/common.utils';
import { useContext } from 'react';
import { StateContext } from '@store/store';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
interface TableOfContentModalPropType {
  toc: TableOfContentType[];
  closeModal: Function;
  modalOpen: boolean;
}

const HEADER_OFFSET_MOBILE = 60;
const HEADER_OFFSET_XL_LG_MD = 80;

const TableOfContentModalMiddle = ({
  toc,
  closeModal,
  modalOpen,
}: TableOfContentModalPropType) => {
  const global = useContext(StateContext);
  const { platform } = global;
  const handleClick = () => closeModal();

  const goToContent = (toc: TableOfContentType) => {
    const element = document.getElementById(toc.id);
    const elementTop = element?.getBoundingClientRect().top ?? 0;

    const headerOffset =
      PlatformType.Mobile === platform
        ? HEADER_OFFSET_MOBILE
        : HEADER_OFFSET_XL_LG_MD;

    const topPosition = elementTop + window.scrollY - headerOffset;
    window.scrollTo({ top: topPosition, behavior: 'smooth' });

    closeModal();
  };

  useEffect(() => {
    if (platform === PlatformType.Tablet || platform === PlatformType.Mobile) {
      if (modalOpen) {
        document.body.style.overflowY = 'hidden';
        document.body.style.paddingRight = '0px';
      }
      return () => {
        document.body.removeAttribute('style');
      };
    }
  }, [modalOpen, platform]);

  return (
    <div
      className={ivclass(
        'iv-fixed',
        'iv-top-0 iv-left-0 iv-z-[25]',
        'iv-bg-black-35',
        'iv-h-full iv-w-full',
        'iv-overflow-x-hidden',
      )}
      id="postContent"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="postContent"
      aria-hidden="true"
    >
      <div
        className={ivclass(
          'iv-flex',
          'iv-items-center',
          'iv-min-h-full iv-min-w-full',
        )}
      >
        <div
          className={ivclass(
            'iv-relative',
            'iv-flex',
            'iv-flex-col iv-items-center iv-justify-center',
            'iv-w-full iv-h-full',
          )}
        >
          <div
            className={ivclass(
              'toc-modal-container',
              'iv-relative',
              'iv-max-w-[551px] iv-w-[calc(var(--w-screen)-40px)]',
              'iv-max-h-[calc(var(--h-screen)-50px)]',
              'iv-h-[470px]',
              'iv-rounded-[15px]',
              'iv-py-40 md:iv-py-30 sm:iv-py-30',
              'iv-pl-40 md:iv-pl-20 sm:iv-pl-20',
              'iv-pr-40 md:iv-pr-20 sm:iv-pr-20',
              'iv-bg-grey-0',
            )}
          >
            <div
              className={ivclass(
                'iv-absolute',
                'iv-z-10',
                'iv-top-[16px] sm:iv-top-[12px] md:iv-top-[12px]',
                'iv-right-[16px] sm:iv-right-[12px] md:iv-right-[12px]',
              )}
            >
              <button
                onClick={handleClick}
                className={ivclass(
                  'iv-flex',
                  'iv-justify-center iv-items-center',
                )}
              >
                <ImageComponent
                  elementype="native"
                  loading="lazy"
                  alt="close icon"
                  src={getAssetUrl('blog', '/icons/toc-close.svg')}
                  width="20"
                  height="20"
                  className={ivclass(
                    'iv-w-[20px] md:iv-w-[16px] sm:iv-w-[16px]',
                    'iv-h-[20px] md:iv-h-[16px] sm:iv-h-[16px]',
                  )}
                />
              </button>
            </div>
            <div
              className={ivclass(
                'iv-h-full',
                'iv-overflow-y-scroll',
                'iv-overflow-x-hidden',
                'iv-overscroll-contain',
              )}
            >
              <div
                className={ivclass(
                  'iv-items-center',
                  'iv-bg-grey-0',
                  'iv-mb-15',
                  'iv-pb-15',
                  'iv-flex iv-gap-15',
                  'iv-sticky',
                  'iv-top-0',
                )}
              >
                <ImageComponent
                  elementype="native"
                  loading="lazy"
                  src={getAssetUrl('blog', '/icons/Left-justify.svg')}
                  className={ivclass(
                    'iv-h-[28px] iv-w-[28px]',
                    'md:iv-h-[20px] md:iv-w-[20px]',
                    'sm:iv-h-[20px] sm:iv-w-[20px]',
                  )}
                />
                <div
                  className={ivclass(
                    'iv-font-bold',
                    'iv-text-blue',
                    'iv-text-heading-2xs iv-m-heading-2xs',
                    'md:iv-text-body-3xl md:iv-m-body-3xl',
                    'sm:iv-text-body-3xl sm:iv-m-body-3xl',
                  )}
                >
                  Post contents
                </div>
              </div>
              {toc.length > 0 &&
                toc.map((content, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      goToContent(content);
                    }}
                    className={ivclass(
                      'iv-flex',
                      'iv-group',
                      'iv-items-start',
                      'iv-mb-[50px] sm:iv-mb-[28px] md:iv-mb-[28px]',
                      'iv-gap-[16px] sm:iv-gap-[12px] md:iv-gap-[12px]',
                      'iv-cursor-default',
                    )}
                  >
                    <div
                      className={ivclass(
                        'iv-mt-3 md:iv-mt-1 sm:iv-mt-1',
                        'iv-ml-1',
                        'sm:iv-w-[21px]',
                        'md:iv-w-[21px]',
                        'iv-w-[28px]',
                      )}
                    >
                      <div
                        className={ivclass(
                          'iv-h-[28px] iv-w-[28px]',
                          'sm:iv-h-[21px] sm:iv-w-[21px]',
                          'md:iv-h-[21px] md:iv-w-[21px]',
                          'iv-flex iv-justify-center iv-items-center',
                          'iv-rounded-[100%]',
                          'iv-bg-black-7',
                          'group-hover:iv-text-blue',
                          'group-hover:iv-bg-blue-20',
                          'iv-text-black-20',
                          'iv-text-body-3xl iv-m-body-3xl',
                          'sm:iv-text-body-l sm:iv-m-body-l',
                          'md:iv-text-body-l md:iv-m-body-l',
                          'iv-font-bold',
                          'iv-min-w-[28px] sm:iv-min-w-[21px] md:iv-min-w-[21px]',
                        )}
                      >
                        {index + 1}
                      </div>
                    </div>
                    <div
                      className={ivclass(
                        'iv-flex iv-flex-col',
                        'iv-gap-y-30 sm:iv-gap-y-20 md:iv-gap-y-20',
                        'iv-min-w-0',
                      )}
                    >
                      <div
                        className={ivclass(
                          'iv-font-medium',
                          'iv-text-black-85',
                          'group-hover:iv-text-blue',
                          'iv-overflow-hidden',
                          'iv-overflow-ellipsis',
                          'iv-whitespace-nowrap',
                          'sm:iv-text-body-3xl sm:iv-m-body-3xl',
                          'md:iv-text-body-3xl md:iv-m-body-3xl',
                          'iv-text-heading-2xs iv-m-heading-2xs',
                        )}
                      >
                        {content.text}
                      </div>
                      {content.children.length > 0 &&
                        content.children.map((subheading, i) => (
                          <div
                            key={i}
                            className={ivclass(
                              'iv-font-medium',
                              'iv-text-black-50',
                              'iv-overflow-hidden',
                              'iv-overflow-ellipsis',
                              'iv-whitespace-nowrap',
                              'sm:iv-text-body-3xl sm:iv-m-body-3xl',
                              'md:iv-text-body-3xl md:iv-m-body-3xl',
                              'iv-text-heading-3xs iv-m-heading-3xs',
                            )}
                          >
                            {`— ${subheading.text}`}
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableOfContentModalMiddle;
